import { resolveExtrudeFeatureKind } from '../../feature/definition/extrude/ExtrudeFeatureKindResolver';
import { hasCurveProfileParams } from './profileSplinePredicate';
import { normalizeExtrudeFeatureDefinition } from './normalizeExtrudeFeatureDefinition';

/**
 * 特征网格化参数。
 */
export interface FeatureTessellationOptions {
  /** 线性离散容差，数值越小网格越贴近真实曲线。 */
  tolerance?: number;
  /** 相对线性挠度系数；提供时不再使用绝对长度容差。 */
  relativeTolerance?: number;
  /** 角度离散容差，单位为度，数值越小弯曲边界越细。 */
  angularTolerance?: number;
}

type Point3 = [number, number, number];

/**
 * 曲线 profile 基础网格化参数。
 *
 * 设计说明：
 * 1. Puppy CAD 内核 BRep 仍是精确曲面，视口显示依赖 BRepMesh 三角化结果；
 * 2. 该值是曲线 profile 的上限，不是最终固定值；
 * 3. 圆、椭圆、圆弧、样条、贝塞尔和 B 样条会继续按自身曲率与采样密度下探。
 */
export const CURVE_PROFILE_TESSELLATION_OPTIONS: Required<
  Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>
> = {
  tolerance: 0.03,
  angularTolerance: 5,
};

/** 曲线 profile 局部细分允许下探到的最小线性容差，保证提交后实体不低于预览曲线贴合度。 */
const CURVE_PROFILE_MIN_TOLERANCE = 0.003;
/** 曲线 profile 局部细分允许下探到的最小角度容差，对齐 360 段预览曲线的显示精度。 */
const CURVE_PROFILE_MIN_ANGULAR_TOLERANCE = 1;
/** 闭合圆锥曲线目标显示段数，避免圆、椭圆与圆环实体提交后退化为低边数多面体。 */
const CURVE_PROFILE_TARGET_FULL_TURN_SEGMENTS = 360;
/**
 * 拉伸凸台曲线轮廓的相对挠度系数。
 *
 * Puppy CAD 内核 的相对模式会再乘以每条边自身尺寸。该无量纲比例只用于维持大小曲线同档显示精度，
 * 不复用草图 360 段采样目标；实际 Puppy CAD 内核 回归将完整圆控制在 48 至 144 个边界点，
 * 同时避免整个合并实体被过度细分。
 */
const BOSS_CURVE_PROFILE_RELATIVE_TOLERANCE = 0.0001;
/** 高密度样条最多按 360 段估算，保持与曲线预览一致的上限同时避免无界增长。 */
const CURVE_PROFILE_MAX_SAMPLE_SEGMENTS = 360;
/** 开放样条最低显示段数。 */
const OPEN_SPLINE_MIN_SAMPLE_SEGMENTS = 160;
/** 闭合样条最低显示段数。 */
const CLOSED_SPLINE_MIN_SAMPLE_SEGMENTS = 240;
/** 控制点对目标段数的贡献系数，避免少量控制点被压低到粗糙显示。 */
const SPLINE_CONTROL_POINT_SEGMENT_MULTIPLIER = 24;
/** 样条容差由目标弦长反推时的缩放系数，控制视觉贴合与生成耗时的平衡。 */
const SPLINE_TOLERANCE_SEGMENT_FACTOR = 12;
const FULL_TURN_RADIANS = Math.PI * 2;
const RADIANS_TO_DEGREES = 180 / Math.PI;
const POINT_EPSILON = 1e-9;

/**
 * 运行时对象守卫。
 *
 * @param value 待判断值。
 * @returns 是否为普通对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * 读取合法的正数网格化参数。
 *
 * @param value 原始参数。
 * @returns 合法正数；无效时返回 null。
 */
function readPositiveFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

/**
 * 限制数值范围。
 *
 * @param value 原始数值。
 * @param min 最小值。
 * @param max 最大值。
 * @returns 限制后的数值。
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * 读取三维点。
 *
 * @param value 原始值。
 * @returns 三维点；无效时返回 null。
 */
function readPoint3(value: unknown): Point3 | null {
  if (!Array.isArray(value) || value.length < 3) {
    return null;
  }
  const point = [Number(value[0]), Number(value[1]), Number(value[2])] as Point3;
  return point.every((item) => Number.isFinite(item)) ? point : null;
}

/**
 * 读取三维点列表。
 *
 * @param value 原始值。
 * @returns 有效三维点列表。
 */
function readPoint3List(value: unknown): Point3[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => readPoint3(item))
    .filter((item): item is Point3 => item !== null);
}

/**
 * 计算两点距离。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 距离。
 */
function computePointDistance(left: Point3, right: Point3): number {
  return Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2]);
}

/**
 * 计算点列折线总长。
 *
 * @param points 点列。
 * @returns 折线长度。
 */
function computePolylineLength(points: Point3[]): number {
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    length += computePointDistance(points[index - 1]!, points[index]!);
  }
  return length;
}

/**
 * 计算点列包围盒对角线。
 *
 * @param points 点列。
 * @returns 包围盒对角线长度。
 */
function computeBoundingDiagonal(points: Point3[]): number {
  if (points.length <= 0) {
    return 0;
  }
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  points.forEach((point) => {
    minX = Math.min(minX, point[0]);
    minY = Math.min(minY, point[1]);
    minZ = Math.min(minZ, point[2]);
    maxX = Math.max(maxX, point[0]);
    maxY = Math.max(maxY, point[1]);
    maxZ = Math.max(maxZ, point[2]);
  });
  return Math.hypot(maxX - minX, maxY - minY, maxZ - minZ);
}

/**
 * 读取曲线闭合状态。
 *
 * @param segment 曲线段。
 * @returns 是否为闭合曲线。
 */
function isClosedSplineLikeSegment(segment: Record<string, unknown>): boolean {
  if (segment.isClosed === true || segment.closureKind === 'closed') {
    return true;
  }
  const points = readPoint3List(segment.curvePoints);
  return points.length >= 2
    && computePointDistance(points[0]!, points[points.length - 1]!) <= POINT_EPSILON;
}

/**
 * 归一化圆弧角度跨度。
 *
 * @param startAngle 起始角。
 * @param endAngle 结束角。
 * @param clockwise 是否顺时针。
 * @returns 角度跨度，单位弧度。
 */
function resolveAngleSpan(startAngle: unknown, endAngle: unknown, clockwise: unknown): number {
  const start = Number(startAngle);
  const end = Number(endAngle);
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return FULL_TURN_RADIANS;
  }
  let span = clockwise === true ? start - end : end - start;
  while (span <= 0) {
    span += FULL_TURN_RADIANS;
  }
  return Math.min(FULL_TURN_RADIANS, span);
}

/**
 * 根据曲率半径估算圆锥曲线网格化参数。
 *
 * @param curvatureRadius 当前曲线最小曲率半径。
 * @param angleSpan 当前曲线角度跨度，单位弧度。
 * @returns 局部网格化参数。
 */
function resolveConicSegmentTessellationOptions(
  curvatureRadius: number,
  angleSpan = FULL_TURN_RADIANS,
): Required<Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>> | null {
  if (!Number.isFinite(curvatureRadius) || curvatureRadius <= 0) {
    return null;
  }
  const normalizedSpan = clamp(Math.abs(angleSpan), POINT_EPSILON, FULL_TURN_RADIANS);
  const targetSegments = Math.max(
    1,
    Math.ceil((CURVE_PROFILE_TARGET_FULL_TURN_SEGMENTS * normalizedSpan) / FULL_TURN_RADIANS),
  );
  const targetAngle = normalizedSpan / targetSegments;
  const sagitta = curvatureRadius * (1 - Math.cos(targetAngle / 2));
  return {
    tolerance: clamp(
      Math.min(CURVE_PROFILE_TESSELLATION_OPTIONS.tolerance, sagitta),
      CURVE_PROFILE_MIN_TOLERANCE,
      CURVE_PROFILE_TESSELLATION_OPTIONS.tolerance,
    ),
    angularTolerance: clamp(
      targetAngle * RADIANS_TO_DEGREES,
      CURVE_PROFILE_MIN_ANGULAR_TOLERANCE,
      CURVE_PROFILE_TESSELLATION_OPTIONS.angularTolerance,
    ),
  };
}

/**
 * 估算椭圆最小曲率半径。
 *
 * @param radiusX X 半轴。
 * @param radiusY Y 半轴。
 * @returns 最小曲率半径。
 */
function resolveEllipseMinimumCurvatureRadius(radiusX: number, radiusY: number): number {
  const majorRadius = Math.max(radiusX, radiusY);
  const minorRadius = Math.min(radiusX, radiusY);
  if (majorRadius <= 0 || minorRadius <= 0) {
    return 0;
  }
  return (minorRadius * minorRadius) / majorRadius;
}

/**
 * 根据样条采样密度估算网格化参数。
 *
 * 设计说明：样条的 Puppy CAD 内核 造型不能改用显示折线，但显示折线已经表达了草图侧的视觉密度；
 * 这里用它推导 BRepMesh 的目标精度，让实体显示网格跟随同一条曲线语义下探。
 *
 * @param segment 样条族 profile 段。
 * @returns 局部网格化参数。
 */
function resolveSplineLikeSegmentTessellationOptions(
  segment: Record<string, unknown>,
): Required<Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>> | null {
  const curvePoints = readPoint3List(segment.curvePoints);
  const controlPoints = readPoint3List(segment.controlPoints);
  const referencePoints = curvePoints.length >= 2 ? curvePoints : controlPoints;
  if (referencePoints.length < 2) {
    return CURVE_PROFILE_TESSELLATION_OPTIONS;
  }

  const isClosed = isClosedSplineLikeSegment(segment);
  const minimumSegments = isClosed
    ? CLOSED_SPLINE_MIN_SAMPLE_SEGMENTS
    : OPEN_SPLINE_MIN_SAMPLE_SEGMENTS;
  const desiredSegments = clamp(
    Math.max(
      referencePoints.length - 1,
      controlPoints.length * SPLINE_CONTROL_POINT_SEGMENT_MULTIPLIER,
      minimumSegments,
    ),
    minimumSegments,
    CURVE_PROFILE_MAX_SAMPLE_SEGMENTS,
  );
  const polylineLength = computePolylineLength(referencePoints);
  const boundingDiagonal = computeBoundingDiagonal(referencePoints);
  const targetChordLength = polylineLength > POINT_EPSILON
    ? polylineLength / desiredSegments
    : 0;
  const toleranceCandidates = [
    boundingDiagonal > POINT_EPSILON
      ? boundingDiagonal / (desiredSegments * SPLINE_TOLERANCE_SEGMENT_FACTOR)
      : null,
    targetChordLength > POINT_EPSILON
      ? targetChordLength / SPLINE_TOLERANCE_SEGMENT_FACTOR
      : null,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0);
  const adaptiveTolerance = toleranceCandidates.length > 0
    ? Math.min(...toleranceCandidates)
    : CURVE_PROFILE_TESSELLATION_OPTIONS.tolerance;

  return {
    tolerance: clamp(
      Math.min(CURVE_PROFILE_TESSELLATION_OPTIONS.tolerance, adaptiveTolerance),
      CURVE_PROFILE_MIN_TOLERANCE,
      CURVE_PROFILE_TESSELLATION_OPTIONS.tolerance,
    ),
    angularTolerance: clamp(
      360 / desiredSegments,
      CURVE_PROFILE_MIN_ANGULAR_TOLERANCE,
      CURVE_PROFILE_TESSELLATION_OPTIONS.angularTolerance,
    ),
  };
}

/**
 * 根据单个曲线 profile 段解析局部网格化参数。
 *
 * @param segment 曲线 profile 段。
 * @returns 局部网格化参数；非曲线段返回 null。
 */
function resolveSegmentTessellationOptions(
  segment: unknown,
): Required<Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>> | null {
  if (!isRecord(segment) || typeof segment.kind !== 'string') {
    return null;
  }
  if (segment.kind === 'circle') {
    return resolveConicSegmentTessellationOptions(Number(segment.radius));
  }
  if (segment.kind === 'arc') {
    return resolveConicSegmentTessellationOptions(
      Number(segment.radius),
      resolveAngleSpan(segment.startAngle, segment.endAngle, segment.clockwise),
    );
  }
  if (segment.kind === 'ellipse') {
    return resolveConicSegmentTessellationOptions(
      resolveEllipseMinimumCurvatureRadius(Number(segment.radiusX), Number(segment.radiusY)),
    );
  }
  if (
    segment.kind === 'spline'
    || segment.kind === 'bezier'
    || segment.kind === 'bspline'
    || segment.curveKind === 'spline'
    || segment.curveKind === 'bezier'
    || segment.curveKind === 'bspline'
  ) {
    return resolveSplineLikeSegmentTessellationOptions(segment);
  }
  return null;
}

/**
 * 从区域几何中收集精确曲线段。
 *
 * @param regionGeometry 区域几何。
 * @returns 曲线段集合。
 */
function collectRegionGeometrySegments(regionGeometry: unknown): unknown[] {
  if (!isRecord(regionGeometry)) {
    return [];
  }
  const segments: unknown[] = [];
  if (Array.isArray(regionGeometry.outerSegments)) {
    segments.push(...regionGeometry.outerSegments);
  }
  if (Array.isArray(regionGeometry.holeSegments)) {
    regionGeometry.holeSegments.forEach((holeSegments) => {
      if (Array.isArray(holeSegments)) {
        segments.push(...holeSegments);
      }
    });
  }
  return segments;
}

/**
 * 从命令参数中收集区域曲线段。
 *
 * @param params 命令参数。
 * @returns 曲线段集合。
 */
function collectRegionProfileSegments(params: Record<string, unknown>): unknown[] {
  const segments = collectRegionGeometrySegments(params.regionGeometry);
  if (Array.isArray(params.regionGeometries)) {
    params.regionGeometries.forEach((regionGeometry) => {
      segments.push(...collectRegionGeometrySegments(regionGeometry));
    });
  }
  return segments;
}

/**
 * 从直接几何来源参数中构造曲线段描述。
 *
 * @param params 命令参数。
 * @returns 曲线段集合。
 */
function collectDirectGeometryProfileSegments(params: Record<string, unknown>): unknown[] {
  switch (params.sourceType) {
    case 'circle_geometry':
      return isRecord(params.circleGeometry)
        ? [{ kind: 'circle', radius: params.circleGeometry.radius }]
        : [];
    case 'ellipse_geometry':
      return isRecord(params.ellipseGeometry)
        ? [{
            kind: 'ellipse',
            radiusX: params.ellipseGeometry.radiusX,
            radiusY: params.ellipseGeometry.radiusY,
          }]
        : [];
    case 'arc_geometry':
      return isRecord(params.arcGeometry)
        ? [{
            kind: 'arc',
            radius: params.arcGeometry.radius,
            startAngle: params.arcGeometry.startAngle,
            endAngle: params.arcGeometry.endAngle,
            clockwise: false,
          }]
        : [];
    case 'spline_geometry':
      return isRecord(params.splineGeometry)
        ? [{ kind: 'spline', ...params.splineGeometry }]
        : [];
    case 'bezier_geometry':
      return isRecord(params.bezierGeometry)
        ? [{ kind: 'spline', curveKind: 'bezier', ...params.bezierGeometry }]
        : [];
    default:
      return [];
  }
}

/**
 * 从特征参数中收集曲线 profile 段。
 *
 * @param params 拉伸或旋转参数。
 * @returns 曲线段集合。
 */
function collectCurveProfileSegments(params: unknown): unknown[] {
  if (!isRecord(params)) {
    return [];
  }
  if (params.sourceType === 'region_geometry') {
    return collectRegionProfileSegments(params);
  }
  return collectDirectGeometryProfileSegments(params);
}

/**
 * 根据曲线 profile 内容解析自适应网格化参数。
 *
 * @param params 拉伸或旋转参数。
 * @returns 自适应网格化参数；缺少曲线细节时返回 null。
 */
function resolveAdaptiveCurveProfileTessellationOptions(
  params: unknown,
): Required<Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>> | null {
  const segmentOptions = collectCurveProfileSegments(params)
    .map((segment) => resolveSegmentTessellationOptions(segment))
    .filter((item): item is Required<
      Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>
    > => item !== null);
  if (segmentOptions.length <= 0) {
    return null;
  }
  return segmentOptions.reduce<Required<
    Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>
  >>((result, options) => ({
    tolerance: Math.min(result.tolerance, options.tolerance),
    angularTolerance: Math.min(result.angularTolerance, options.angularTolerance),
  }), CURVE_PROFILE_TESSELLATION_OPTIONS);
}

/**
 * 合并调用方参数与曲线 profile。
 *
 * 设计说明：曲线 profile 一旦命中专用参数，就不能再被外部粗参数反向覆盖；
 * 如果调用方显式传入更细的参数，则保留调用方更细配置。
 *
 * @param options 调用方参数。
 * @param profileOptions 曲线 profile 参数。
 * @returns 曲线 profile 实际使用的网格化参数。
 */
export function mergeCurveProfileTessellationOptions(
  options: FeatureTessellationOptions | undefined,
  profileOptions: Required<
    Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>
  > = CURVE_PROFILE_TESSELLATION_OPTIONS,
): Required<Pick<FeatureTessellationOptions, 'tolerance' | 'angularTolerance'>> {
  const callerTolerance = readPositiveFiniteNumber(options?.tolerance);
  const callerAngularTolerance = readPositiveFiniteNumber(options?.angularTolerance);

  return {
    tolerance: callerTolerance === null
      ? profileOptions.tolerance
      : Math.min(callerTolerance, profileOptions.tolerance),
    angularTolerance: callerAngularTolerance === null
      ? profileOptions.angularTolerance
      : Math.min(callerAngularTolerance, profileOptions.angularTolerance),
  };
}

/**
 * 根据特征参数解析实际网格化参数。
 *
 * @param options 调用方参数。
 * @param params 拉伸或旋转命令参数。
 * @returns 实际网格化参数；非曲线 profile 保持调用方参数。
 */
export function resolveCurveProfileTessellationOptions(
  options: FeatureTessellationOptions | undefined,
  params: unknown,
): FeatureTessellationOptions | undefined {
  if (!hasCurveProfileParams(params)) {
    return options;
  }
  return mergeCurveProfileTessellationOptions(
    options,
    resolveAdaptiveCurveProfileTessellationOptions(params) ?? CURVE_PROFILE_TESSELLATION_OPTIONS,
  );
}

/**
 * 判断拉伸参数是否属于相对挠度策略的唯一适用范围。
 *
 * @param params 拉伸参数。
 * @returns 是否为非薄壁、带精确曲线轮廓的拉伸凸台。
 */
function shouldUseBossCurveRelativeTessellation(params: unknown): params is ExtrudeParams {
  if (!isRecord(params) || !hasCurveProfileParams(params)) {
    return false;
  }
  const extrudeParams = params as unknown as ExtrudeParams;
  return resolveExtrudeFeatureKind(extrudeParams) === 'boss-extrude'
    && !normalizeExtrudeFeatureDefinition(extrudeParams).thinFeature.enabled;
}

/**
 * 解析拉伸凸台专用网格化参数。
 *
 * 设计说明：
 * 1. 精确曲线凸台使用边尺寸相关的相对挠度，预览与提交都由最终 shape 的唯一网格化调用执行；
 * 2. 绝对容差与相对系数单位不同，不能直接比较，因此曲线凸台不继承调用方的绝对 tolerance；
 * 3. 拉伸切除、薄壁拉伸和非曲线轮廓继续复用既有绝对容差策略。
 *
 * @param options 调用方网格化参数。
 * @param params 拉伸参数。
 * @returns 拉伸凸台实际使用的网格化参数。
 */
export function resolveBossExtrudeTessellationOptions(
  options: FeatureTessellationOptions | undefined,
  params: unknown,
): FeatureTessellationOptions | undefined {
  if (!shouldUseBossCurveRelativeTessellation(params)) {
    return resolveCurveProfileTessellationOptions(options, params);
  }

  const callerRelativeTolerance = readPositiveFiniteNumber(options?.relativeTolerance);
  const callerAngularTolerance = readPositiveFiniteNumber(options?.angularTolerance);

  return {
    relativeTolerance: callerRelativeTolerance === null
      ? BOSS_CURVE_PROFILE_RELATIVE_TOLERANCE
      : Math.min(callerRelativeTolerance, BOSS_CURVE_PROFILE_RELATIVE_TOLERANCE),
    angularTolerance: callerAngularTolerance === null
      ? CURVE_PROFILE_TESSELLATION_OPTIONS.angularTolerance
      : Math.min(callerAngularTolerance, CURVE_PROFILE_TESSELLATION_OPTIONS.angularTolerance),
  };
}
